import React from 'react';
import { Download, CheckCircle2, FileText, Sparkles, Lock, Unlock } from 'lucide-react';
import { KeywordResearchProject } from '../../types';
import { KeywordPdfService } from '../../services/keywordPdfService';
import { Button } from '../UI/Button';
import { Badge } from '../UI/Badge';

interface DualReportExportWorkstationProps {
  project: KeywordResearchProject;
}

export const DualReportExportWorkstation: React.FC<DualReportExportWorkstationProps> = ({
  project,
}) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      
      {/* SUCCESS TOP BANNER */}
      <div style={{ textAlign: 'center', padding: '24px', background: 'rgba(15, 23, 42, 0.9)', border: '1px solid var(--accent-green)', borderRadius: 'var(--radius-md)' }}>
        <CheckCircle2 size={48} color="var(--accent-green)" style={{ marginBottom: '10px' }} />
        <h3 style={{ fontSize: '20px', color: '#ffffff', fontWeight: 800, marginBottom: '6px' }}>
          Keyword Research & SEO Architecture Strategy Complete!
        </h3>
        <p style={{ fontSize: '13px', color: 'var(--text-muted)', maxWidth: '650px', margin: '0 auto' }}>
          You have successfully completed the 16-step keyword research and optimization workflow for <strong>{project.clientName}</strong> ({project.businessProfile.websiteUrl || 'Project Website'}). Select an export report below:
        </p>
      </div>

      {/* DUAL REPORT EXPORT CARDS GRID */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '20px' }}>
        
        {/* CARD 1: CLIENT EXECUTIVE PRESENTATION REPORT */}
        <div style={{ background: '#020617', border: '1px solid #facc15', borderRadius: 'var(--radius-md)', padding: '24px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <Badge variant="lecture">Client Deliverable</Badge>
              <Lock size={16} color="#facc15" />
            </div>

            <div style={{ fontSize: '16px', fontWeight: 800, color: '#ffffff', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Sparkles size={20} color="#facc15" />
              <span>CLIENT EXECUTIVE PRESENTATION REPORT (PDF)</span>
            </div>

            <p style={{ fontSize: '12px', color: '#cbd5e1', lineHeight: '1.5', marginBottom: '16px' }}>
              An elegant, high-impact executive presentation designed to wows and impress the client. Communicates strategic value, market coverage, and search visibility.
            </p>

            <div style={{ background: 'rgba(234, 179, 8, 0.08)', border: '1px solid rgba(234, 179, 8, 0.2)', borderRadius: 'var(--radius-sm)', padding: '12px', marginBottom: '20px', fontSize: '11px', color: '#e2e8f0', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div>• <strong>Includes:</strong> Business Profile, Market Coverage, Finalized Target Keywords, Page Mapping & SERP Previews.</div>
              <div>• <strong>Focus:</strong> What & Why (Strategic value & growth roadmap).</div>
              <div>• 🔒 <strong>Protected:</strong> Hides internal agency prompts, secret formulas, and raw density logs.</div>
            </div>
          </div>

          <Button
            variant="primary"
            style={{ background: '#facc15', color: '#000', fontWeight: 800, width: '100%' }}
            icon={<Download size={16} />}
            onClick={() => KeywordPdfService.exportClientPresentationPdf(project)}
          >
            Export Client Executive Presentation PDF
          </Button>
        </div>

        {/* CARD 2: INTERNAL WORKER TECHNICAL OPERATIONS REPORT */}
        <div style={{ background: '#020617', border: '1px solid #38bdf8', borderRadius: 'var(--radius-md)', padding: '24px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <Badge variant="tool">Internal Agency Manual</Badge>
              <Unlock size={16} color="#38bdf8" />
            </div>

            <div style={{ fontSize: '16px', fontWeight: 800, color: '#ffffff', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FileText size={20} color="#38bdf8" />
              <span>INTERNAL WORKER TECHNICAL OPERATIONS REPORT (PDF)</span>
            </div>

            <p style={{ fontSize: '12px', color: '#cbd5e1', lineHeight: '1.5', marginBottom: '16px' }}>
              Complete technical operations manual for agency review, project managers, and internal content writers. Includes all insider strategies and tactical details.
            </p>

            <div style={{ background: 'rgba(56, 189, 248, 0.08)', border: '1px solid rgba(56, 189, 248, 0.2)', borderRadius: 'var(--radius-sm)', padding: '12px', marginBottom: '20px', fontSize: '11px', color: '#e2e8f0', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div>• <strong>Includes:</strong> All seed variations, cannibalization guards, 500w/1000w keyword density ratios (0.5%).</div>
              <div>• <strong>Technical Details:</strong> Exact H2 question placements, direct answer copy, JSON-LD schema code & prompt logs.</div>
              <div>• ⚙️ <strong>Operations:</strong> Allows full agency review of what was done, how, and why.</div>
            </div>
          </div>

          <Button
            variant="secondary"
            style={{ borderColor: '#38bdf8', color: '#38bdf8', fontWeight: 800, width: '100%' }}
            icon={<Download size={16} />}
            onClick={() => KeywordPdfService.exportInternalTechnicalPdf(project)}
          >
            Export Internal Worker Technical Report PDF
          </Button>
        </div>
      </div>
    </div>
  );
};
